import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ReceiptText, X, Plus, Trash2, Printer, Send, RotateCcw, Save } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { to12h } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReceiptDialog({ booking, token, onClose }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [r, setR] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => axios.get(`${API}/bookings/${booking.id}/receipt`, H).then(({ data }) => {
    setR(data); setLines(data.lines); setNotes(data.notes || "");
  }).catch(() => toast.error("Could not load receipt."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [booking.id]);

  const charges = lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  const balance = charges - (r?.discount || 0) - (r?.amount_paid || 0);
  const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const save = async (silent = false) => {
    setBusy("save");
    try {
      const { data } = await axios.put(`${API}/bookings/${booking.id}/receipt`, { lines, notes }, H);
      setR(data); setLines(data.lines);
      if (!silent) toast.success("Receipt saved.");
      return data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); return null; }
    finally { setBusy(""); }
  };
  const print = async () => {
    const data = await save(true);
    if (data?.token) window.open(`/receipt/${data.token}`, "_blank");
  };
  const send = async () => {
    if (!window.confirm(`Send this receipt to ${booking.name} (${booking.email || "no email"}${booking.phone ? ` / ${booking.phone}` : ""}) now?`)) return;
    setBusy("send");
    try {
      await save(true);
      const { data } = await axios.post(`${API}/bookings/${booking.id}/receipt/send`, {}, H);
      toast.success(`Receipt sent — email ${data.emailed ? "✓" : "✗"}, SMS ${data.smsed ? "✓" : "✗"}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed."); }
    finally { setBusy(""); }
  };
  const reset = async () => {
    if (!window.confirm("Reset the receipt to the automatic breakdown? Your custom edits will be discarded.")) return;
    setBusy("reset");
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/receipt/reset`, {}, H);
      setR(data); setLines(data.lines); setNotes(data.notes || "");
      toast.success("Receipt reset to automatic breakdown.");
    } catch { toast.error("Reset failed."); }
    finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto py-8 px-3" data-testid={`receipt-dialog-${booking.id}`}>
      <div className="w-full max-w-2xl bg-[#0C0C0C] border border-[#F2B705]/25 rounded-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <span className="text-white font-semibold flex items-center gap-2"><ReceiptText size={17} className="text-[#F2B705]" /> Receipt {r?.receipt_no || ""} — {booking.name}</span>
          <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="receipt-close"><X size={18} /></button>
        </div>
        {!r ? <div className="flex justify-center py-14"><Loader2 className="animate-spin text-[#F2B705]" /></div> : (
          <div className="p-5 space-y-4">
            <div className="text-xs text-white/45">{r.vehicle} · {r.pickup_date} {to12h(r.pickup_time)} → {r.return_date} {to12h(r.return_time)} · {r.days} day{r.days !== 1 ? "s" : ""}
              {r.sent_at && <span className="text-green-400 ml-2">✓ last sent {fmtDateTime(r.sent_at)}</span>}</div>

            <div className="space-y-1.5" data-testid="receipt-lines">
              {lines.map((l, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input value={l.label} onChange={(e) => setLine(i, "label", e.target.value)} data-testid={`receipt-line-label-${i}`}
                    className="flex-1 bg-black border border-white/15 rounded px-2.5 py-1.5 text-white text-sm" />
                  <input type="number" step="0.01" value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} data-testid={`receipt-line-amount-${i}`}
                    className="w-28 bg-black border border-white/15 rounded px-2.5 py-1.5 text-white text-sm text-right" />
                  <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="text-white/30 hover:text-red-400" data-testid={`receipt-line-del-${i}`}><Trash2 size={14} /></button>
                </div>
              ))}
              <button onClick={() => setLines([...lines, { label: "", amount: 0 }])} data-testid="receipt-add-line"
                className="flex items-center gap-1.5 text-xs text-[#F2B705] hover:text-white mt-1"><Plus size={12} /> Add line (late fee, cleaning, credit…)</button>
            </div>

            <div className="border-t border-white/10 pt-3 text-sm space-y-1">
              <div className="flex justify-between text-white/70"><span>Total charges</span><b className="text-white">{money(charges)}</b></div>
              {r.discount > 0 && <div className="flex justify-between text-white/70"><span>Discounts / credits</span><b className="text-green-400">-{money(r.discount)}</b></div>}
              <div className="flex justify-between text-white/70"><span>Amount paid</span><b className="text-green-400">-{money(r.amount_paid)}</b></div>
              <div className="flex justify-between text-base"><span className="text-white font-semibold">Balance</span><b className={balance > 0.005 ? "text-red-400" : "text-green-400"} data-testid="receipt-balance">{money(balance)}</b></div>
            </div>

            {r.payments?.length > 0 && (
              <div className="text-xs text-white/45">
                <div className="uppercase tracking-wider text-white/35 mb-1">Payments received</div>
                {r.payments.map((p, i) => <div key={i} className="flex justify-between"><span>{p.date} · {p.type}</span><span className="text-white/70">{money(p.amount)}</span></div>)}
              </div>
            )}

            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note to customer (optional — shows on the receipt)"
              data-testid="receipt-notes" rows={2} className="w-full bg-black border border-white/15 rounded px-2.5 py-1.5 text-white text-sm" />

            <div className="flex flex-wrap gap-2 pt-1">
              <button onClick={() => save(false)} disabled={!!busy} data-testid="receipt-save-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-white/10 text-white text-sm font-semibold hover:bg-white/20 disabled:opacity-50">
                {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
              <button onClick={print} disabled={!!busy} data-testid="receipt-print-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-white/10 text-white text-sm font-semibold hover:bg-white/20 disabled:opacity-50">
                <Printer size={13} /> Print
              </button>
              <button onClick={send} disabled={!!busy} data-testid="receipt-send-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black text-sm font-bold disabled:opacity-50">
                {busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send to Customer
              </button>
              {r.custom && (
                <button onClick={reset} disabled={!!busy} data-testid="receipt-reset-btn"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/15 text-white/60 text-sm hover:text-white ml-auto disabled:opacity-50">
                  {busy === "reset" ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Reset to auto
                </button>
              )}
            </div>
            <p className="text-white/30 text-[10px]">A receipt is also sent automatically when you mark the rental returned.</p>
          </div>
        )}
      </div>
    </div>
  );
}
